import React, { useEffect, useState } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity, ActivityIndicator,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { useOwnerScope } from '../../../src/context/OwnerScopeContext';
import { useTheme } from '../../../src/context/ThemeContext';
import { useNetwork } from '../../../src/context/NetworkContext';
import { useToast } from '../../../src/components/Toast';
import ContentContainer from '../../../src/components/ContentContainer';
import { AdminToolbar } from '../../../src/components/AdminList';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import type { ColorTheme } from '../../../src/utils/theme';
import PageFooter from '../../../src/components/PageFooter';

interface OrderItem { id: string; productName: string; quantity: number; priceInCents: number }
interface Order {
  id: string; status: string; totalInCents: number;
  createdAt: string; items: OrderItem[];
}

const STATUS_COLORS: Record<string, string> = {
  pending: '#eab308', processing: '#3b82f6', shipped: '#8b5cf6',
  delivered: '#059669', cancelled: '#dc2626',
};

export default function OrderHistory() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();
  const { isOffline } = useNetwork();
  const router = useRouter();
  const scope = useOwnerScope();
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchOrders = async (p = page, searchOverride?: string) => {
    try {
      const params = new URLSearchParams({ page: String(p), limit: '10' });
      const s = searchOverride ?? search;
      if (s) params.set('search', s);
      const data = await scope.api.api<{ orders: Order[]; total: number }>(`/api/hardware/orders?${params}`);
      setOrders(data.orders);
      setTotal(data.total);
      setPage(p);
    } catch (err: any) {
      if (!isOffline) {
        toast.show({ type: 'error', title: 'Couldn’t load orders', message: err?.message ?? 'Try again in a moment.' });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(1); }, []);

  const renderOrder = ({ item }: { item: Order }) => {
    const statusColor = STATUS_COLORS[item.status] || colors.textSecondary;
    return (
      <TouchableOpacity
        style={styles.card}
        onPress={() => router.push(`${scope.basePath}/shop/orders/${item.id}` as never)}
        activeOpacity={0.7}
      >
        <View style={styles.cardHeader}>
          <Text style={styles.date}>{new Date(item.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}</Text>
          <View style={[styles.badge, { backgroundColor: statusColor + '20' }]}>
            <Text style={[styles.badgeText, { color: statusColor }]}>
              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
            </Text>
          </View>
        </View>
        <Text style={styles.itemsSummary}>
          {item.items.map(i => `${i.productName} x${i.quantity}`).join(', ')}
        </Text>
        <View style={styles.cardFooter}>
          <Text style={styles.total}>${(item.totalInCents / 100).toFixed(2)}</Text>
          <Ionicons name="chevron-forward" size={18} color={colors.textLight} />
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <ContentContainer role="owner" footer={false}>
      <View style={styles.container}>
        <Text style={styles.title}>Order History</Text>
        <AdminToolbar
          search={search}
          onSearchChange={setSearch}
          onSearch={() => fetchOrders(1)}
          total={total}
          page={page}
          limit={10}
          onPageChange={(p) => fetchOrders(p)}
          csvEndpoint={`/api/hardware/orders${search ? `?search=${encodeURIComponent(search)}` : ''}`}
          csvFilename="hardware-orders.csv"
          label="order"
        />
        {loading ? (
          <ActivityIndicator size="large" color={colors.primary} style={{ marginTop: spacing.xl }} />
        ) : orders.length === 0 ? (
          <View style={styles.emptyState}>
            <Ionicons name="receipt-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyTitle}>{search ? 'No Orders Found' : 'No Orders Yet'}</Text>
            <Text style={styles.emptyText}>{search ? 'Try a different search.' : 'Your hardware orders will appear here.'}</Text>
          </View>
        ) : (
          <FlatList
            ListFooterComponent={<PageFooter />}
            data={orders}
            keyExtractor={item => item.id}
            renderItem={renderOrder}
            contentContainerStyle={{ paddingBottom: spacing.xl }}
          />
        )}
      </View>
    </ContentContainer>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, padding: spacing.md },
  title: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text, marginBottom: spacing.md },
  card: {
    backgroundColor: c.surface, borderRadius: borderRadius.md, padding: spacing.md,
    marginBottom: spacing.sm, borderWidth: 1, borderColor: c.borderLight,
  },
  cardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs },
  date: { fontSize: fontSize.sm, color: c.textSecondary },
  badge: { paddingHorizontal: 10, paddingVertical: 3, borderRadius: 12 },
  badgeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold },
  itemsSummary: { fontSize: fontSize.sm, color: c.text, marginBottom: spacing.xs },
  cardFooter: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  total: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  emptyState: { alignItems: 'center', paddingTop: 80 },
  emptyTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginTop: spacing.md },
  emptyText: { fontSize: fontSize.sm, color: c.textSecondary, textAlign: 'center', maxWidth: 300, marginTop: spacing.xs },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
